import { useMemo, useState } from "react";
import { usePortfolio } from "@/lib/PortfolioProvider";
import WorkCard from "@/components/site/WorkCard";

/**
 * Cinematic Editorial Exhibition rhythm.
 *
 * Index 0 is a large full-bleed feature. The remainder flow as asymmetric
 * 2-ups with alternating direction and MIXED aspect ratios (16:10, 4:5, 3:4,
 * 3:2), interrupted every few items by a full-width cinematic moment (21:9).
 * The layout is gap-safe: a trailing lone item is promoted to full width so a
 * 12-col row is never left with an empty cell (Taste "exact cell count").
 * Everything collapses to a single column below `lg`.
 *
 * Column classes are LITERAL strings (COL map) so Tailwind's JIT detects them.
 */
const COL = { 5: "lg:col-span-5", 7: "lg:col-span-7", 12: "lg:col-span-12" };

// Non-feature cell templates, cycled. Each pair sums to 12; the [12] is a
// deliberate full-width cinematic interruption.
const SEQ = [
    { span: 7, ar: "aspect-[16/10]" },
    { span: 5, ar: "aspect-[4/5]" },
    { span: 5, ar: "aspect-[3/4]" },
    { span: 7, ar: "aspect-[3/2]" },
    { span: 12, ar: "aspect-[16/10] lg:aspect-[21/9]", kind: "wide" },
];

const buildShapes = (count) => {
    const shapes = [];
    if (count <= 0) return shapes;
    shapes.push({ col: COL[12], ar: "aspect-[16/11] lg:aspect-[2/1]", kind: "feature" });
    let col = 0;
    for (let i = 1; i < count; i += 1) {
        const tpl = SEQ[(i - 1) % SEQ.length];
        let span = tpl.span;
        let ar = tpl.ar;
        let kind = tpl.kind;
        if (col + span > 12) col = 0;
        const isLast = i === count - 1;
        if (isLast && col === 0 && span < 12) {
            span = 12;
            ar = "aspect-[16/10] lg:aspect-[21/9]";
            kind = "wide";
        }
        shapes.push({ col: COL[span], ar, kind });
        col = (col + span) % 12;
    }
    return shapes;
};

/**
 * WorkEditorial — renders the exhibition. `items` (homepage preview) renders
 * exactly those; otherwise it reads the CMS portfolio and, with `showFilters`,
 * exposes category filtering.
 */
export const WorkEditorial = ({ items: itemsProp, showFilters = false }) => {
    const { branding, categories } = usePortfolio();
    const [active, setActive] = useState("All");

    const items = useMemo(() => {
        if (itemsProp) return itemsProp;
        if (active === "All") return branding || [];
        return (branding || []).filter((p) => p.category === active);
    }, [itemsProp, active, branding]);

    const shapes = useMemo(() => buildShapes(items.length), [items.length]);

    return (
        <>
            {showFilters && (
                <div
                    className="mb-10 flex flex-wrap gap-2"
                    role="tablist"
                    aria-label="Filter work by category"
                    data-testid="work-filters"
                >
                    {(categories || ["All"]).map((cat) => {
                        const isActive = active === cat;
                        return (
                            <button
                                key={cat}
                                onClick={() => setActive(cat)}
                                role="tab"
                                aria-selected={isActive}
                                data-testid={`portfolio-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                                className="pg-app-tap pg-filter-pill"
                                data-active={isActive ? "true" : "false"}
                            >
                                {cat}
                            </button>
                        );
                    })}
                </div>
            )}

            {items.length === 0 ? (
                <div
                    className="mt-6 rounded-2xl border p-10 text-center sm:p-14"
                    style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
                    data-testid="portfolio-empty-state"
                >
                    <p className="font-display text-xl sm:text-2xl" style={{ color: "var(--pg-fg)" }}>
                        No projects in this category yet.
                    </p>
                    <button
                        type="button"
                        onClick={() => setActive("All")}
                        data-testid="portfolio-empty-view-all"
                        className="pg-btn-ghost mt-6"
                    >
                        View all work
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-2 items-start gap-x-3 gap-y-6 sm:gap-x-6 sm:gap-y-10 lg:grid-cols-12 lg:gap-x-8 lg:gap-y-16">
                    {items.map((item, idx) => (
                        <WorkCard key={item.id || idx} item={item} shape={shapes[idx] || {}} idx={idx} />
                    ))}
                </div>
            )}
        </>
    );
};

export default WorkEditorial;
